import _ from "lodash";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {Grid, Stack, Typography} from "@mui/material";
import {KeyValuePair} from "../base/KeyValuePair";
import {NotificationCard} from "./NotificationCard";
import {UserInvitationEx, UserInvitationStatus} from "../../types/domain/userInvitation";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {acceptUserInvitation, rejectUserInvitation} from "../../services/invitationService";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceLoading} from "../../utils/resourceUtils";

export interface Props {
    invitation: UserInvitationEx;
    onRefresh: () => void;
}

export const InvitationNotificationCard: React.FC<Props> = ({invitation, onRefresh}) => {
    const {t} = useTranslation();
    const invitationManagement = useResourceManagement({acceptUserInvitation, rejectUserInvitation});

    const onAccept = () => {
        invitationManagement.reset();
        invitationManagement
            .acceptUserInvitation(invitation.id)
            .then(() => onRefresh())
            .catch(swallowException);
    };

    const onReject = () => {
        invitationManagement.reset();
        invitationManagement
            .rejectUserInvitation(invitation.id)
            .then(() => onRefresh())
            .catch(swallowException);
    };

    return (
        <NotificationCard
            header={t("notifications.invitation.title", {companyName: invitation.companyName})}
            date={new Date(invitation.createdAt).toLocaleDateString()}
            isPending={invitation.status === UserInvitationStatus.PENDING}
            isLoading={isResourceLoading(invitationManagement.resource)}
            error={getResourceErrorMessage(invitationManagement.resource)}
            onAccept={onAccept}
            onReject={onReject}
        >
            <Stack direction="column" gap="1.5rem">
                <Typography>{getInvitationMessage(invitation, t)}</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <KeyValuePair header={t("notifications.invitation.company_name")}>
                            <Typography>{invitation.companyName}</Typography>
                        </KeyValuePair>
                    </Grid>
                    <Grid item xs={12}>
                        <KeyValuePair header={t("notifications.invitation.role")}>
                            <Typography>{_.capitalize(invitation.role)}</Typography>
                        </KeyValuePair>
                    </Grid>
                </Grid>
            </Stack>
        </NotificationCard>
    );
};

const getInvitationMessage = (invitation: UserInvitationEx, t: TFunction<string, undefined>) => {
    if (invitation.status === UserInvitationStatus.PENDING) {
        return t("notifications.invitation.status.pending", {companyName: invitation.companyName});
    } else if (invitation.status === UserInvitationStatus.ACCEPTED) {
        return t("notifications.invitation.status.accepted", {companyName: invitation.companyName});
    } else if (invitation.status === UserInvitationStatus.REJECTED) {
        return t("notifications.invitation.status.rejected", {companyName: invitation.companyName});
    } else {
        return null;
    }
};
